import { ActionType, createReducer } from 'typesafe-actions';

import { AssetGroupType } from 'common/api/autogenerated/trading-gateway';

import assetsActionTypes from './assets.actionTypes';
import * as assetsActions from './assets.actions';
import { initialState } from './assets.initialState';
import {
  applyTrendToAsset,
  mapAssetsFromServer,
  mapCoinsGroupsFromServer,
  mapCoinsToAssets,
  mapFavoriteAssetsFromServer,
  mapFavoriteCoinsFromServer,
  mapGroupsFromServer,
  syncPricesChanges,
} from './assets.mapper';

type State = typeof initialState;
type Actions = ActionType<typeof assetsActions>;

export const assetsReducer = createReducer<State, Actions>(initialState)
  .handleAction(assetsActions.actionGetAssets, (state) => ({
    ...state,
    assetsLoading: true,
    loading: true,
  }))
  .handleAction(assetsActions.actionGetCoins, (state) => ({
    ...state,
    coinsLoading: true,
  }))
  .handleAction(assetsActions.actionSuccessGetAssets, (state, action) => {
    const assets = mapAssetsFromServer(action.payload.data || []);
    return {
      ...state,
      assets,
      assetsLoading: false,
      loading: false,
      initialLoad: false,
      error: false,
    };
  })
  .handleAction(assetsActions.actionSuccessGetCoins, (state, action) => {
    const coins = mapCoinsToAssets(action.payload.data || []);
    return {
      ...state,
      coins,
      coinsLoading: false,
    };
  })
  .handleAction(assetsActions.actionErrorGetAssets, (state) => ({
    ...state,
    error: true,
    assetsLoading: false,
    loading: false,
  }))
  .handleAction(assetsActions.actionSuccessGetSpotTrend, (state, action) => {
    const data = applyTrendToAsset(action.payload, state.assets);

    return {
      ...state,
      assets: data,
    };
  })
  .handleAction(assetsActions.actionSuccessGetSpotCommissions, (state, action) => ({
    ...state,
    commissions: {
      ...state.commissions,
      ...action.payload,
    },
  }))
  .handleAction(assetsActions.actionGetAssetsGroups, (state) => ({
    ...state,
    assetsGroupsLoading: true,
    loading: true,
  }))
  .handleAction(assetsActions.actionGetCoinsGroups, (state) => ({
    ...state,
    coinGroupsLoading: true,
    loading: true,
  }))
  .handleAction(assetsActions.actionSuccessGetAssetsGroups, (state, action) => {
    const groupsAssets = mapGroupsFromServer(action.payload.data || []);
    return {
      ...state,
      groupsAssets,
      assetsGroupsLoading: false,
      loading: false,
      error: false,
    };
  })
  .handleAction(assetsActions.actionSuccessGetCoinsGroups, (state, action) => {
    const groupsCoins = mapCoinsGroupsFromServer(action.payload || []);
    return {
      ...state,
      groupsCoins,
      coinGroupsLoading: false,
      loading: false,
      error: false,
    };
  })
  .handleAction(assetsActions.actionErrorGetAssetsGroups, (state) => ({
    ...state,
    error: true,
    assetsGroupsLoading: false,
    loading: false,
  }))
  .handleAction(assetsActions.actionGetFavoriteAssets, (state) => ({
    ...state,
    assetsFavoritesLoading: state.initialLoad,
    loading: true,
  }))
  .handleAction(assetsActions.actionSuccessGetFavoriteAssets, (state, action) => {
    const favoritesAssets = mapFavoriteAssetsFromServer(action.payload.data || []);

    return {
      ...state,
      assetsFavoritesLoading: false,
      loading: false,
      favoritesAssets,
      groupsAssets: {
        ...state.groupsAssets,
        [AssetGroupType.FAVORITES]: {
          type: AssetGroupType.FAVORITES,
          symbols: favoritesAssets,
        },
      },
    };
  })
  .handleAction(assetsActions.actionErrorGetFavoriteAssets, (state) => ({
    ...state,
    assetsFavoritesLoading: false,
    loading: false,
  }))
  .handleAction(assetsActions.actionGetFavoriteCoins, (state) => ({
    ...state,
    coinsFavoritesLoading: true,
  }))
  .handleAction(assetsActions.actionSuccessGetFavoriteCoins, (state, action) => {
    const favoritesCoins = mapFavoriteCoinsFromServer(action.payload.data || []);
    return {
      ...state,
      coinsFavoritesLoading: false,
      favoritesCoins,
    };
  })
  .handleAction(assetsActions.actionErrorGetFavoriteCoins, (state) => ({
    ...state,
    coinsFavoritesLoading: false,
  }))
  .handleAction(
    [
      assetsActions.actionReceiveSignalRLastPrice,
      assetsActions.actionReceiveSignalRBestAsk,
      assetsActions.actionReceiveSignalRBestBid,
    ],
    (state, action) => {
      const { symbol: assetSymbol, price } = action.payload;
      const [coinSymbol] = assetSymbol.split('/');
      const assets = { ...state.assets };
      const coins = { ...state.coins };
      const stateAsset = assets[assetSymbol];
      const stateCoin = coins[coinSymbol];

      if (action.type === assetsActionTypes.SIGNALR_INCOME_RECEIVE_BESTBID) {
        if (stateAsset) {
          stateAsset.prevBid = stateAsset.bid;
          stateAsset.bid = price;
        }
        if (stateCoin) {
          stateCoin.prevBid = stateCoin.bid;
          stateCoin.bid = price;
        }
      }
      if (action.type === assetsActionTypes.SIGNALR_INCOME_RECEIVE_BESTASK) {
        if (stateAsset) {
          stateAsset.prevAsk = stateAsset.ask;
          stateAsset.ask = price;
        }
        if (stateCoin) {
          stateCoin.prevAsk = stateCoin.ask;
          stateCoin.ask = price;
        }
      }
      if (action.type === assetsActionTypes.SIGNALR_INCOME_RECEIVE_LASTPRICE) {
        if (stateAsset) {
          stateAsset.prevPrice = stateAsset.price;
          stateAsset.price = price;
        }
        if (stateCoin) {
          stateCoin.prevPrice = stateCoin.price;
          stateCoin.price = price;
        }
      }

      if (assets[assetSymbol]) {
        assets[assetSymbol] = { ...stateAsset };
      }

      if (coins[coinSymbol]) {
        coins[coinSymbol] = { ...stateCoin };
      }

      const pricesChanges =
        state.info.symbol === assetSymbol ? syncPricesChanges(price, { ...state.info.pricesChanges }) : state.info;

      return {
        ...state,
        assets,
        coins,
        info: {
          ...state.info,
          ...pricesChanges,
        },
      };
    },
  )
  .handleAction(assetsActions.actionGetAssetInfo, (state, action) => ({
    ...state,
    assetInfoLoading: true,
    loading: true,
    info: { ...initialState.info, symbol: action.payload },
  }))
  .handleAction(assetsActions.actionSuccessGetAssetInfo, (state, action) => ({
    ...state,
    info: {
      ...state.info,
      ...action.payload,
    },
    assetInfoLoading: false,
    loading: false,
    initialLoad: false,
    error: false,
  }))
  .handleAction(assetsActions.actionErrorGetAssetInfo, (state) => ({
    ...state,
    error: true,
    assetInfoLoading: false,
    loading: false,
  }));
